/**
 * The amounts an invoice is paid on. An approver's Kürzung lowers the net amount; VAT and gross
 * follow at the same ratio, so the invoice's rate is kept, and the printed amounts stay as read.
 * The cost allocation adds up to the net amount this returns, and a payment file pays its gross.
 */

/** What reading an invoice's payable amounts needs of it; amounts as numbers or numeric strings. */
export interface ReducibleAmounts {
	netAmount: number | string | null;
	vatAmount: number | string | null;
	grossAmount: number | string | null;
	reducedNetAmount: number | string | null;
}

/**
 * The net, VAT and gross amounts the invoice is paid on, and the ratio a Kürzung applied — 1 for
 * an invoice that is not reduced.
 *
 * @param invoice - The printed amounts and the reduced net amount, if any.
 * @returns The amounts, null where the printed one is unknown.
 */
export function getPayableAmounts(invoice: ReducibleAmounts) {
	const net = toNumber(invoice.netAmount);
	const vat = toNumber(invoice.vatAmount);
	const gross = toNumber(invoice.grossAmount);
	const reduced = toNumber(invoice.reducedNetAmount);
	if (reduced === null || net === null || net <= 0 || reduced >= net) {
		return { netAmount: net, vatAmount: vat, grossAmount: gross, ratio: 1, isReduced: false };
	}
	const ratio = reduced / net;
	const vatAmount = vat === null ? null : roundCents(vat * ratio);
	const grossAmount =
		vatAmount !== null
			? roundCents(reduced + vatAmount)
			: gross === null
				? null
				: roundCents(gross * ratio);
	return { netAmount: reduced, vatAmount, grossAmount, ratio, isReduced: true };
}

/**
 * Scale amounts that add up to one total so they add up to another, to the cent — what a Kürzung
 * does to a cost allocation. The rounding remainder goes to the largest share.
 *
 * @param amounts - The amounts, in order.
 * @param total - The total they are to add up to.
 * @returns The scaled amounts, in the same order.
 */
export function scaleToTotal(amounts: number[], total: number) {
	let sum = 0;
	for (const amount of amounts) sum += amount;
	if (!amounts.length || sum === 0) return amounts.map(() => 0);
	const scaled = amounts.map((amount) => roundCents((amount / sum) * total));
	let scaledSum = 0;
	let largest = 0;
	for (let index = 0; index < scaled.length; index++) {
		scaledSum += scaled[index] ?? 0;
		if ((scaled[index] ?? 0) > (scaled[largest] ?? 0)) largest = index;
	}
	scaled[largest] = roundCents((scaled[largest] ?? 0) + (total - scaledSum));
	return scaled;
}

function roundCents(value: number) {
	return Math.round(value * 100) / 100;
}

function toNumber(value: number | string | null) {
	if (value === null || value === "") return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}
